/** Operator picker for a namespace declaring more than one operator.
 *  The parent skips rendering this stage entirely when there is exactly one. */
import { List, ListItemButton, ListItemText, Paper, Popper } from '@mui/material';

import type { QueryOperator } from './types';

/** Operator names, shared by this menu and the chip editor's Operator step. `idmatch`
 *  (the FE-only match-by-ID operator) is never offered here, but a chip can carry it. */
export const OP_LABEL: Record<QueryOperator | 'idmatch', string> = {
  exact: 'equals (case-sensitive)',
  iexact: 'equals',
  icontains: 'contains',
  range: 'range (from..to)',
  gt: 'greater than (v..)',
  lt: 'less than (..v)',
  idmatch: 'is this record (by ID)',
};

/** An operator's name, or the operator itself when it has none. */
export function opLabel(op: string): string {
  return (OP_LABEL as Record<string, string | undefined>)[op] ?? op;
}

interface Props {
  anchorEl: HTMLElement | null;
  open: boolean;
  operators: QueryOperator[];
  highlightedIndex: number;
  onHighlightChange: (index: number) => void;
  onSelect: (op: QueryOperator) => void;
}

export function OperatorMenu({
  anchorEl,
  open,
  operators,
  highlightedIndex,
  onHighlightChange,
  onSelect,
}: Props) {
  return (
    <Popper
      open={open && Boolean(anchorEl)}
      anchorEl={anchorEl}
      placement="bottom-start"
      style={{ zIndex: 1300 }}
    >
      <Paper elevation={4} sx={{ mt: 0.5, minWidth: 220 }}>
        <List role="listbox" dense disablePadding>
          {operators.map((op, i) => (
            <ListItemButton
              key={op}
              role="option"
              aria-selected={i === highlightedIndex}
              selected={i === highlightedIndex}
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect(op);
              }}
              onMouseEnter={() => onHighlightChange(i)}
            >
              <ListItemText primary={OP_LABEL[op]} secondary={op} />
            </ListItemButton>
          ))}
        </List>
      </Paper>
    </Popper>
  );
}
